import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Input } from '@components/forms/Input';
import { Select } from '@components/forms/Select';
import { Checkbox } from '@components/forms/Checkbox';
import { FormCard } from '@components/forms/FormCard';
import { ProgressSteps } from '@components/forms/ProgressSteps';
import { Button } from '@components/common/Button';
import { ErrorAlert } from '@components/common/ErrorAlert';
import { TermsModal } from '@components/auth/TermsModal';
import { registrationSchema, type RegistrationFormData, nigerianStates } from '@utils/validation';
import { useRegisterMutation } from '@store/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from '@store/authSlice';
import type { RootState } from '@store/index';
import { extractErrorMessage, extractFieldErrors, logError, isValidationError } from '@utils/errorHandling';
import { Calendar, IdCard, Mail, MapPin, User, Lock } from 'lucide-react';

const steps = [
  { id: 'personal', title: 'Personal Info', description: 'Your name and identity', icon: <User className="h-5 w-5" /> },
  { id: 'contact', title: 'Contact', description: 'How we reach you', icon: <Mail className="h-5 w-5" /> },
  { id: 'address', title: 'Address', description: 'Where you live', icon: <MapPin className="h-5 w-5" /> },
  { id: 'security', title: 'Security', description: 'Password & terms', icon: <Lock className="h-5 w-5" /> },
] as const;

type StepId = typeof steps[number]['id'];

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState<StepId>('personal');
  const [completedSteps, setCompletedSteps] = useState<StepId[]>([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const [registerUser, { isLoading }] = useRegisterMutation();

  // Redirect authenticated users
  React.useEffect(() => {
    if (isAuthenticated && user) {
      // Route based on user role
      switch (user.role) {
        case 'APPLICANT':
          navigate('/dashboard', { replace: true });
          break;
        case 'ADMIN':
        case 'OPS':
          navigate('/admin', { replace: true });
          break;
        default:
          navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const defaultValues: RegistrationFormData = useMemo(() => ({
    first_name: '',
    middle_name: '',
    last_name: '',
    nin: '',
    date_of_birth: '',
    nationality: 'Nigerian',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Nigeria',
    employer: '',
    password: '',
    confirm_password: '',
    accept_terms: false,
  }), []);

  const { register, handleSubmit, formState: { errors }, trigger, watch, setError, clearErrors } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = async (data: RegistrationFormData) => {
    try {
      // Clear previous errors
      setApiError('');
      clearErrors();
      
      const payload = {
        ...data,
      };
      const res = await registerUser(payload).unwrap();
      dispatch(setCredentials({ user: res.user, access_token: res.access_token }));
      // Route APPLICANTs to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      // Log error for debugging
      logError(err, 'Registration Error');
      
      // Handle validation errors (422)
      if (isValidationError(err)) {
        const fieldErrors = extractFieldErrors(err);
        Object.entries(fieldErrors).forEach(([field, message]) => {
          setError(field as keyof RegistrationFormData, { message });
        });
        
        // If no field-specific errors, show general validation message
        if (Object.keys(fieldErrors).length === 0) {
          setApiError(extractErrorMessage(err));
        }
      } else {
        // Handle other errors (400, 500, etc.)
        setApiError(extractErrorMessage(err));
      }
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleRetry = () => {
    setApiError('');
    clearErrors();
  };
  
  const handleDismissError = () => {
    setApiError('');
  };

  const password = watch('password');
  const acceptTerms = watch('accept_terms');

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleTermsDecline = () => {
    setTermsAccepted(false);
    setShowTermsModal(false);
  };

  const handleNext = async () => {
    // Validate current step fields before proceeding
    const fieldsByStep: Record<StepId, (keyof RegistrationFormData)[]> = {
      personal: ['first_name', 'last_name', 'nin', 'date_of_birth', 'nationality'],
      contact: ['email', 'phone'],
      address: ['address_line1', 'city', 'state', 'country', 'postal_code'],
      security: ['password', 'confirm_password', 'accept_terms'],
    };

    const fields = fieldsByStep[currentStep];
    const valid = await trigger(fields as any, { shouldFocus: true });
    if (!valid) return;

    const currentIndex = steps.findIndex(s => s.id === currentStep);
    const nextStep = steps[currentIndex + 1]?.id;
    if (nextStep) {
      setCompletedSteps(prev => Array.from(new Set([...prev, currentStep])) as StepId[]);
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    const prevStep = steps[currentIndex - 1]?.id;
    if (prevStep) setCurrentStep(prevStep);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-ncaa-dark">Create Your Account</h1>
        <p className="text-ncaa-medium mt-2">Register to start your application process</p>
      </div>

      <ProgressSteps steps={steps as any} currentStep={currentStep} completedSteps={completedSteps} className="mb-8" />

      {apiError && (
        <ErrorAlert
          title="Registration Failed"
          message={apiError}
          variant="error"
          onRetry={handleRetry}
          onDismiss={handleDismissError}
          retryLabel="Try Again"
          className="mb-6"
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {currentStep === 'personal' && (
          <FormCard
            title="Personal Information"
            description="Enter your legal name and identity details"
            icon={<IdCard className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                label="First Name"
                required
                {...register('first_name')}
                error={errors.first_name?.message}
                placeholder="John"
              />
              <Input
                label="Middle Name"
                {...register('middle_name')}
                error={errors.middle_name?.message}
                placeholder="(Optional)"
              />
              <Input
                label="Last Name"
                required
                {...register('last_name')}
                error={errors.last_name?.message}
                placeholder="Doe"
              />

              <Input
                label="National Identification Number (NIN)"
                required
                {...register('nin')}
                error={errors.nin?.message}
                placeholder="11-digit NIN"
                inputMode="numeric"
                maxLength={11}
              />

              <Input
                label="Date of Birth"
                type="date"
                required
                {...register('date_of_birth')}
                error={errors.date_of_birth?.message}
              />

              <Input
                label="Nationality"
                {...register('nationality')}
                error={errors.nationality?.message as any}
                placeholder="Nigerian"
              />
            </div>
          </FormCard>
        )}

        {currentStep === 'contact' && (
          <FormCard
            title="Contact Information"
            description="How we can reach you"
            icon={<Mail className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Email"
                type="email"
                required
                {...register('email')}
                error={errors.email?.message}
                placeholder="you@example.com"
              />

              <Input
                label="Phone Number"
                required
                {...register('phone')}
                error={errors.phone?.message}
                placeholder="08012345678"
                inputMode="tel"
              />
            </div>
          </FormCard>
        )}

        {currentStep === 'address' && (
          <FormCard
            title="Address Information"
            description="Your residential address"
            icon={<MapPin className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Address Line 1"
                required
                {...register('address_line1')}
                error={errors.address_line1?.message}
                placeholder="Street address, P.O. box, company name, c/o"
              />

              <Input
                label="Address Line 2"
                {...register('address_line2')}
                error={errors.address_line2?.message}
                placeholder="Apartment, suite, unit, building, floor"
              />

              <Input
                label="City"
                required
                {...register('city')}
                error={errors.city?.message}
                placeholder="Lagos"
              />

              <Select
                label="State"
                required
                {...register('state')}
                error={errors.state?.message}
                options={nigerianStates.map((s) => ({ value: s, label: s }))}
                placeholder="Select a state"
              />

              <Input
                label="Postal Code"
                {...register('postal_code')}
                error={errors.postal_code?.message}
                placeholder="6-digit code"
                inputMode="numeric"
                maxLength={6}
              />

              <Input
                label="Country"
                {...register('country')}
                error={errors.country?.message as any}
                placeholder="Nigeria"
              />

              <Input
                label="Employer (Optional)"
                {...register('employer')}
                error={errors.employer?.message}
                placeholder="Your current employer"
                className="md:col-span-2"
              />
            </div>
          </FormCard>
        )}

        {currentStep === 'security' && (
          <FormCard
            title="Security & Terms"
            description="Set up a secure password and agree to terms"
            icon={<Lock className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Password"
                type="password"
                required
                {...register('password')}
                error={errors.password?.message}
                placeholder="Create a strong password"
              />
              <Input
                label="Confirm Password"
                type="password"
                required
                {...register('confirm_password')}
                error={errors.confirm_password?.message}
                placeholder="Re-enter your password"
              />
            </div>

            <div className="mt-4">
              {/* Lightweight inline strength hints; full component is available if needed */}
              <div className="text-sm text-gray-600">Use at least 8 characters, including upper/lowercase, a number and a special character.</div>
            </div>

            <div className="mt-6 space-y-3">
              <div>
                <Button type="button" variant="outline" onClick={() => setShowTermsModal(true)}>
                  Read Terms and Conditions
                </Button>
              </div>
              <Checkbox
                label={
                  <span>
                    I have read and agree to the Terms and Conditions
                  </span>
                } as any
                {...register('accept_terms')}
                error={errors.accept_terms?.message}
              />
            </div>
          </FormCard>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between pt-2">
          <Button type="button" variant="outline" onClick={handleBack} disabled={currentStep === 'personal'}>
            Back
          </Button>

          {currentStep !== 'security' ? (
            <Button type="button" onClick={handleNext}>
              Continue
            </Button>
          ) : (
            <Button type="submit" loading={isLoading}>
              Create Account
            </Button>
          )}
        </div>
      </form>

      {/* Terms and Conditions Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </div>
  );
};
